/**
 * End-to-End Encryption Utility for Chat Messages
 * Uses Web Crypto API for browser-based encryption
 * Similar to WhatsApp's encryption model
 */

class ChatEncryption {
  constructor() {
    this.keyPairs = new Map(); // Store key pairs per conversation
    this.publicKeys = new Map(); // Store public keys of other users
  }

  /**
   * Generate a key pair for the current user
   */
  async generateKeyPair(userId) {
    try {
      const keyPair = await window.crypto.subtle.generateKey(
        {
          name: 'RSA-OAEP',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-256',
        },
        true, // extractable
        ['encrypt', 'decrypt']
      );

      this.keyPairs.set(userId, keyPair);
      
      // Export public key for sharing
      const publicKey = await this.exportPublicKey(keyPair.publicKey);
      
      // Store in localStorage for persistence
      this.saveKeyPairToStorage(userId, keyPair);
      
      return { keyPair, publicKey };
    } catch (error) {
      console.error('Failed to generate key pair:', error);
      throw error;
    }
  }

  /**
   * Export public key to base64 string
   */
  async exportPublicKey(publicKey) {
    const exported = await window.crypto.subtle.exportKey('spki', publicKey);
    const exportedAsString = String.fromCharCode.apply(null, new Uint8Array(exported));
    return window.btoa(exportedAsString);
  }

  /**
   * Import public key from base64 string
   */
  async importPublicKey(publicKeyString) {
    try {
      const binaryString = window.atob(publicKeyString);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return await window.crypto.subtle.importKey(
        'spki',
        bytes,
        {
          name: 'RSA-OAEP',
          hash: 'SHA-256',
        },
        true,
        ['encrypt']
      );
    } catch (error) {
      console.error('Failed to import public key:', error);
      throw error;
    }
  }

  /**
   * Generate session key for symmetric encryption (AES)
   */
  async generateSessionKey() {
    return await window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt message using hybrid encryption (RSA + AES)
   */
  async encryptMessage(message, recipientPublicKey) {
    try {
      // Generate session key for this message
      const sessionKey = await this.generateSessionKey();

      // Encrypt message with session key (AES)
      const encoder = new TextEncoder();
      const messageData = encoder.encode(message);
      
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const encryptedMessage = await window.crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv,
        },
        sessionKey,
        messageData
      );

      // Export session key
      const exportedSessionKey = await window.crypto.subtle.exportKey('raw', sessionKey);

      // Encrypt session key with recipient's public key (RSA)
      const publicKey = await this.importPublicKey(recipientPublicKey);
      const encryptedSessionKey = await window.crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP',
        },
        publicKey,
        exportedSessionKey
      );

      // Combine everything into a single object
      return {
        encryptedMessage: this.arrayBufferToBase64(encryptedMessage),
        encryptedSessionKey: this.arrayBufferToBase64(encryptedSessionKey),
        iv: this.arrayBufferToBase64(iv),
        encrypted: true,
      };
    } catch (error) {
      console.error('Encryption failed:', error);
      throw error;
    }
  }

  /**
   * Decrypt message using hybrid decryption
   */
  async decryptMessage(encryptedData, userId) {
    try {
      // Get user's private key
      const keyPair = this.keyPairs.get(userId) || await this.loadKeyPairFromStorage(userId);
      
      if (!keyPair) {
        throw new Error('Private key not found');
      }

      // Decrypt session key with private key (RSA)
      const encryptedSessionKey = this.base64ToArrayBuffer(encryptedData.encryptedSessionKey);
      const sessionKeyBuffer = await window.crypto.subtle.decrypt(
        {
          name: 'RSA-OAEP',
        },
        keyPair.privateKey,
        encryptedSessionKey
      );

      // Import session key
      const sessionKey = await window.crypto.subtle.importKey(
        'raw',
        sessionKeyBuffer,
        {
          name: 'AES-GCM',
          length: 256,
        },
        true,
        ['decrypt']
      );

      // Decrypt message with session key (AES)
      const encryptedMessage = this.base64ToArrayBuffer(encryptedData.encryptedMessage);
      const iv = this.base64ToArrayBuffer(encryptedData.iv);
      
      const decryptedMessage = await window.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
        },
        sessionKey,
        encryptedMessage
      );

      // Decode message
      const decoder = new TextDecoder();
      return decoder.decode(decryptedMessage);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw error;
    }
  }

  /**
   * Helper: Convert ArrayBuffer to Base64
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Helper: Convert Base64 to ArrayBuffer
   */
  base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Save key pair to localStorage
   */
  async saveKeyPairToStorage(userId, keyPair) {
    try {
      const publicKey = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
      const privateKey = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

      localStorage.setItem(`chat_public_key_${userId}`, this.arrayBufferToBase64(publicKey));
      localStorage.setItem(`chat_private_key_${userId}`, this.arrayBufferToBase64(privateKey));
    } catch (error) {
      console.error('Failed to save key pair:', error);
    }
  }

  /**
   * Load key pair from localStorage
   */
  async loadKeyPairFromStorage(userId) {
    try {
      const publicKeyString = localStorage.getItem(`chat_public_key_${userId}`);
      const privateKeyString = localStorage.getItem(`chat_private_key_${userId}`);

      if (!publicKeyString || !privateKeyString) {
        return null;
      }

      const publicKeyBuffer = this.base64ToArrayBuffer(publicKeyString);
      const privateKeyBuffer = this.base64ToArrayBuffer(privateKeyString);

      const publicKey = await window.crypto.subtle.importKey(
        'spki',
        publicKeyBuffer,
        {
          name: 'RSA-OAEP',
          hash: 'SHA-256',
        },
        true,
        ['encrypt']
      );

      const privateKey = await window.crypto.subtle.importKey(
        'pkcs8',
        privateKeyBuffer,
        {
          name: 'RSA-OAEP',
          hash: 'SHA-256',
        },
        true,
        ['decrypt']
      );

      const keyPair = { publicKey, privateKey };
      this.keyPairs.set(userId, keyPair);
      
      return keyPair;
    } catch (error) {
      console.error('Failed to load key pair:', error);
      return null;
    }
  }

  /**
   * Initialize encryption for a user
   */
  async initializeEncryption(userId) {
    // Try to load existing keys
    let keyPair = await this.loadKeyPairFromStorage(userId);
    
    // Generate new keys if none exist
    if (!keyPair) {
      const result = await this.generateKeyPair(userId);
      return result.publicKey;
    }

    // Export existing public key
    return await this.exportPublicKey(keyPair.publicKey);
  }

  /**
   * Store recipient's public key
   */
  storePublicKey(recipientId, publicKey) {
    this.publicKeys.set(recipientId, publicKey);
    // Also persist to localStorage
    localStorage.setItem(`chat_recipient_key_${recipientId}`, publicKey);
  }

  /**
   * Get recipient's public key
   */
  getPublicKey(recipientId) {
    let publicKey = this.publicKeys.get(recipientId);
    
    if (!publicKey) {
      // Try to load from localStorage
      publicKey = localStorage.getItem(`chat_recipient_key_${recipientId}`);
      if (publicKey) {
        this.publicKeys.set(recipientId, publicKey);
      }
    }
    
    return publicKey;
  }

  /**
   * Clear all encryption data (for logout)
   */
  clearAllKeys() {
    this.keyPairs.clear();
    this.publicKeys.clear();
    
    // Clear from localStorage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('chat_public_key_') || 
          key.startsWith('chat_private_key_') || 
          key.startsWith('chat_recipient_key_')) {
        localStorage.removeItem(key);
      }
    });
  }
}

// Export singleton instance
const chatEncryption = new ChatEncryption();
export default chatEncryption;

